import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ksh } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

// Adaptive polling: 3s for the first 10 checks, 5s for the next 10, then 8s.
const POLL_MAX = 40;
const intervalFor = (n) => (n < 10 ? 3000 : n < 20 ? 5000 : 8000);

// KCB Buni / M-Pesa result-code messages (mirrors gwshop's mapping).
const ERROR_MESSAGES = {
  "1": "Insufficient M-Pesa balance. Top up and try again.",
  "1032": "Payment was cancelled on your phone.",
  "1037": "The request timed out — you didn't enter your PIN in time.",
  "1019": "The request timed out. Please try again.",
  "26": "M-Pesa is busy right now. Please try again shortly.",
};
const messageFor = (code) =>
  ERROR_MESSAGES[String(code)] || "The payment didn't go through. Please try again.";

const STAGES = ["Initiated", "Sent", "Processing", "Complete"];

export default function CheckoutModal({ open, onClose }) {
  const { items, totalCents, clear } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("form"); // form | initiating | pending | success | failed
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [error, setError] = useState("");
  const [checkoutId, setCheckoutId] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [progress, setProgress] = useState(0);

  // Reset and prefill from the signed-in user whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setStep("form");
      setError("");
      setPhoneError("");
      setCheckoutId(null);
      setStatusData(null);
      setOrderRef(null);
      setProgress(0);
      setName(user?.name || "");
      setEmail(user?.email || "");
      setPhone(user?.phone || "");
    }
  }, [open, user]);

  // Polling loop while we wait for the Buni callback to settle the payment.
  useEffect(() => {
    if (step !== "pending" || !checkoutId) return;
    let cancelled = false;
    let count = 0;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      try {
        const s = await api.paymentStatus(checkoutId);
        if (cancelled) return;
        setStatusData(s);
        if (s.status === "completed") {
          setProgress(100);
          setStep("success");
          clear();
          return;
        }
        if (["failed", "cancelled", "timeout"].includes(s.status)) {
          setError(messageFor(s.result_code));
          setStep("failed");
          return;
        }
      } catch {
        // transient error — keep polling
      }
      count += 1;
      setProgress(Math.min(90, 20 + count * 4));
      if (count >= POLL_MAX) {
        setError("We didn't get a confirmation in time. If you paid, contact us with your order reference.");
        setStep("failed");
        return;
      }
      timer = setTimeout(tick, intervalFor(count));
    };

    timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [step, checkoutId, clear]);

  if (!open) return null;

  const validPhone = (p) => p.replace(/\D/g, "").length >= 9;

  const start = async () => {
    if (!validPhone(phone)) {
      setPhoneError("Enter a valid M-Pesa phone number (e.g. 0712 345 678).");
      return;
    }
    setPhoneError("");
    setError("");
    setStep("initiating");
    setProgress(10);

    const res = await api.createOrder({
      phone,
      customer_name: name,
      email,
      items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
    });

    if (res.ok && res.data.checkout_request_id) {
      setOrderRef(res.data.order_reference);
      setCheckoutId(res.data.checkout_request_id);
      setProgress(20);
      setStep("pending");
    } else {
      setOrderRef(res.data.order_reference);
      setError(res.data.error || "We couldn't start the payment. Please try again.");
      setStep("failed");
    }
  };

  const activeStage = { initiating: 1, pending: 2, success: 4, failed: 0 }[step] ?? 0;

  return (
    <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && step !== "pending" && onClose()}>
      <div className="modal" style={{ maxWidth: "480px" }}>
        <div className="modal-hero">
          {step !== "pending" && (
            <button className="modal-close" onClick={onClose}>
              &times;
            </button>
          )}
          <div className="ic">
            <i className="fa-solid fa-mobile-screen-button" />
          </div>
          <h2>Checkout</h2>
          <p>Pay securely with M-Pesa via an STK push to your phone.</p>
        </div>

        <div className="modal-body">
          {step === "form" && (
            <>
              <div className="checkout-summary">
                {items.map((i) => (
                  <div className="ci-row" key={i.id}>
                    <span>
                      {i.name} × {i.qty}
                    </span>
                    <span>Ksh {ksh(i.price * i.qty)}</span>
                  </div>
                ))}
                <div className="ci-row total">
                  <span>Total</span>
                  <span>Ksh {ksh(totalCents / 100)}</span>
                </div>
              </div>

              <div className="fg">
                <label>Your name (optional)</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div className="fg">
                <label>Email for receipt (optional)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div className="fg">
                <label>M-Pesa phone number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="07xx xxx xxx"
                />
                {phoneError && <div className="field-error">{phoneError}</div>}
              </div>

              <div className="checkout-actions">
                <button className="btn btn-leaf" onClick={start} disabled={items.length === 0}>
                  <i className="fa-solid fa-mobile-screen-button" /> Pay Ksh {ksh(totalCents / 100)}
                </button>
              </div>
            </>
          )}

          {(step === "initiating" || step === "pending") && (
            <>
              <div className="pay-stages">
                {STAGES.map((label, i) => (
                  <div className={`pay-stage${i < activeStage ? " active" : ""}`} key={label}>
                    <div className="dot">
                      {i < activeStage - 1 ? <i className="fa-solid fa-check" /> : i + 1}
                    </div>
                    {label}
                  </div>
                ))}
              </div>
              <div className="pay-progress">
                <div className="bar" style={{ width: `${progress}%` }} />
              </div>
              <div className="pay-state">
                <div className="big-ic spin">
                  <i className="fa-solid fa-spinner" />
                </div>
                <h3>{step === "initiating" ? "Sending request…" : "Check your phone"}</h3>
                <p>
                  {step === "initiating"
                    ? "Creating your order and contacting M-Pesa."
                    : "An STK push was sent. Enter your M-Pesa PIN to approve the payment."}
                </p>
                {orderRef && <p style={{ marginTop: "8px" }}>Order ref: <b>{orderRef}</b></p>}
              </div>
            </>
          )}

          {step === "success" && (
            <div className="pay-state">
              <div className="big-ic ok">
                <i className="fa-solid fa-circle-check" />
              </div>
              <h3>Payment successful!</h3>
              <p>Thank you — we've received your payment and will be in touch about delivery.</p>
              <div className="pay-receipt">
                <div>
                  <span>Order</span>
                  <b>{statusData?.order_reference || orderRef}</b>
                </div>
                {statusData?.mpesa_receipt && (
                  <div>
                    <span>M-Pesa receipt</span>
                    <b>{statusData.mpesa_receipt}</b>
                  </div>
                )}
                <div>
                  <span>Amount</span>
                  <b>Ksh {ksh(statusData?.amount || totalCents / 100)}</b>
                </div>
              </div>
              <div className="checkout-actions">
                <a
                  className="btn btn-ghost"
                  href={api.invoiceUrl(statusData?.order_reference || orderRef)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-solid fa-file-arrow-down" /> Download invoice
                </a>
                <button className="btn btn-leaf" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}

          {step === "failed" && (
            <div className="pay-state">
              <div className="big-ic err">
                <i className="fa-solid fa-circle-exclamation" />
              </div>
              <h3>Payment not completed</h3>
              <p>{error}</p>
              {orderRef && <p style={{ marginTop: "8px" }}>Order ref: <b>{orderRef}</b></p>}
              <div className="checkout-actions">
                <button className="btn btn-ghost" style={{ color: "var(--forest)", borderColor: "var(--line)" }} onClick={onClose}>
                  Close
                </button>
                <button className="btn btn-leaf" onClick={() => setStep("form")}>
                  <i className="fa-solid fa-rotate-right" /> Try again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
